import React, { useRef, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { DateTimePicker } from '@modernloop/shared/components';
import { IsoTimestamp, assertIsoTimestamp, startOfDay } from '@modernloop/shared/datetime';
import { CaretDownIcon } from '@modernloop/shared/icons';
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  MenuItemProps,
  Paper,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers';
import { parseISO } from 'date-fns';

import DurationMenu, { MaxDuration, getDurationLabel, getDurationOptions } from 'src/components/DurationMenu';

import { IS_PRODUCTION } from 'src/constants';

type Props = {
  anchorEl: React.RefObject<HTMLElement>;
  duration: number;
  utcTime: Date;
  timezone: string;
  showTimePicker?: boolean;
  utcAvailability?: number[];
  maxDuration?: MaxDuration;
  timezoneSelectComponent?: JSX.Element;
  hideTimePicker?: boolean;
  onChange: (duration: number, utcNewStartAt: Date | null) => void;
  clearTime: () => void;
  onClose: () => void;
};

const SET_START_TIME_OPTION_ID = 'set-start-time';

const DurationTimePicker = ({
  anchorEl,
  duration,
  utcTime,
  timezone,
  showTimePicker,
  utcAvailability,
  maxDuration,
  timezoneSelectComponent,
  hideTimePicker,
  onChange,
  clearTime,
  onClose,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [showDatePicker, setShowDatePicker] = useState(Boolean(showTimePicker));
  const [showDurationPicker, setShowDurationPicker] = useState(false);
  const [newDuration, setNewDuration] = useState(duration);
  const [newUtcTime, setNewUtcTime] = useState(utcTime);

  const durationPickerRef = useRef<HTMLButtonElement>(null);

  if (!showDatePicker) {
    let options = getDurationOptions().map((option) => {
      if (Number(option.id) === duration) {
        return { ...option, selected: true };
      }
      return option;
    });

    options = maxDuration
      ? options.filter((option) => {
          const num = Number(option.id);
          return num && !Number.isNaN(num) && num <= maxDuration && num !== 0;
        })
      : options;

    if (!IS_PRODUCTION) {
      options = [{ id: '1', value: '1 min [NOT-SHOWN-IN-PROD]' }, ...options];
    }

    if (!hideTimePicker) {
      options.unshift({
        id: SET_START_TIME_OPTION_ID,
        value: t('hiring-dashboard-interviewers:interviewers.plan.durationTimePicker.setStartTime'),
      });
    }

    const handleDurationSelect = (option: MenuItemProps) => {
      if (option.id === SET_START_TIME_OPTION_ID) {
        setShowDatePicker(true);
        return;
      }

      if (option.id && !Number.isNaN(Number(option.id))) {
        onChange(Number(option.id), null);
      }
      onClose();
    };

    return (
      <Menu
        anchorEl={anchorEl.current}
        id="duration-time-picker"
        sx={{ maxHeight: '480px' }}
        disableScrollLock
        open
        onClose={(e, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            onClose();
          }
        }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.id}
            onClick={() => handleDurationSelect(option)}
            sx={{ color: option.id === SET_START_TIME_OPTION_ID ? 'primary.main' : undefined }}
          >
            {option.value}
          </MenuItem>
        ))}
      </Menu>
    );
  }

  const durationText =
    getDurationLabel(newDuration) ||
    t('hiring-dashboard-interviewers:interviewers.plan.durationTimePicker.minutes', { count: newDuration });

  const handleClose = () => {
    onChange(newDuration, newUtcTime);
    onClose();
  };

  const handleClear = () => {
    clearTime();
    onClose();
  };

  return (
    <Popover
      anchorEl={anchorEl.current}
      open
      onClose={handleClose}
      anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      disableScrollLock
    >
      <Paper sx={{ p: 2 }}>
        <Stack direction="column" spacing={2}>
          <Typography color="primary" variant="subtitle2">
            {t('hiring-dashboard-interviewers:interviewers.plan.durationTimePicker.startTime')}
          </Typography>
          {timezoneSelectComponent}
          <Stack spacing={1} direction="row" alignItems="center">
            <Box>
              <DateTimePicker
                time={assertIsoTimestamp(newUtcTime.toISOString())}
                timezone={timezone}
                onChange={(time: IsoTimestamp) => setNewUtcTime(parseISO(time))}
                disablePast
                renderDay={(props) => {
                  const startOfDayInTimezone = startOfDay(props.day, timezone);
                  const isAvailabilityDay = utcAvailability?.includes(parseISO(startOfDayInTimezone).getTime());
                  return (
                    <Box sx={{ flexGrow: 1 }}>
                      <PickersDay
                        {...(props as PickersDayProps<IsoTimestamp>)}
                        sx={{
                          ...(props.sx as object),
                          border: isAvailabilityDay ? (theme) => `1px solid ${theme.palette.primary.main}` : undefined,
                          color: isAvailabilityDay ? (theme) => theme.palette.primary.main : undefined,
                        }}
                      />
                    </Box>
                  );
                }}
              />
            </Box>
            <Button variant="text" color="primary" onClick={handleClear}>
              {t('hiring-dashboard-interviewers:interviewers.plan.durationTimePicker.clear')}
            </Button>
          </Stack>
          <Typography color="primary" variant="subtitle2">
            {t('hiring-dashboard-interviewers:interviewers.plan.durationTimePicker.duration')}
          </Typography>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Button sx={{ borderRadius: '6px' }} ref={durationPickerRef} onClick={() => setShowDurationPicker(true)}>
              {durationText}
            </Button>
            <IconButton onClick={() => setShowDurationPicker(true)}>
              <CaretDownIcon />
            </IconButton>
          </Stack>
          <DurationMenu
            id="duration-time-picker"
            open={showDurationPicker}
            anchorEl={durationPickerRef}
            maxDuration={maxDuration}
            onClose={() => setShowDurationPicker(false)}
            onSelect={(mins: number) => {
              setNewDuration(mins);
              setShowDurationPicker(false);
            }}
          />
        </Stack>
      </Paper>
    </Popover>
  );
};

export default DurationTimePicker;
